<script lang="ts">
  import { tenantCtx } from '../../../lib/tenant.svelte';
  import { session } from '../../../lib/auth.svelte';
  import { effectiveOrgTipo } from '../../../lib/clinic-preview.svelte';
  import type { Tab } from '../../../lib/navigation';
  import {
    CLINIC_PLUS_MODULES,
    VET_PLUS_MODULES,
    OPTICA_PLUS_MODULES,
  } from '../../../lib/clinic-plus';

  let { onNavigate }: { onNavigate: (tab: string) => void } = $props();

  const org = $derived(
    effectiveOrgTipo(
      tenantCtx.orgTipo as import('../../../lib/navigation').OrgTipo | null,
      tenantCtx.isHospitalEdition,
      session.value?.tenantId,
    ),
  );

  const isPlus = $derived(
    tenantCtx.plan === 'clinica-plus' || tenantCtx.edicion === 'clinic-network',
  );

  const catalog = $derived.by(() => {
    if (org === 'clinica-veterinaria') return VET_PLUS_MODULES;
    if (org === 'clinica-optica') return OPTICA_PLUS_MODULES;
    return CLINIC_PLUS_MODULES;
  });

  const visible = $derived.by(() => {
    const mods = tenantCtx.modulosActivos;
    return catalog.filter(m => {
      if (!mods?.length) return true;
      const modMap: Partial<Record<Tab, string>> = {
        agenda: 'citas', pacientes: 'admision', expediente: 'expediente', signos: 'expediente',
        laboratorio: 'laboratorio', imagenologia: 'imagenologia', farmaciaPos: 'facturacion',
        cuentas: 'facturacion', aseguradoras: 'facturacion', bodega: 'bodega', tesoreria: 'tesoreria',
        usuarios: 'admision', configuracion: 'admision',
        veterinaria: 'veterinaria', expedienteVet: 'veterinaria', vetInternamiento: 'veterinaria',
        vetMostrador: 'veterinaria', vetFarmacia: 'veterinaria',
        optica: 'optica', opticaOrdenes: 'optica', opticaInventario: 'optica',
      };
      const mod = modMap[m.tab];
      if (!mod) return true;
      return mods.includes(mod);
    });
  });
</script>

{#if isPlus && visible.length}
  <section class="plus-panel" aria-label="Módulos incluidos en su plan">
    <header>
      <div>
        <span class="plus-badge">Clínica Plus</span>
        <h2>Todo lo que incluye su plan</h2>
        <p>Haga clic en un módulo para ir directo. Sin pantallas ocultas ni extras sorpresa.</p>
      </div>
    </header>
    <div class="mod-grid">
      {#key visible.length}
      {#each visible as m (m.tab)}
        <button type="button" class="mod-card" onclick={() => onNavigate(m.tab)}>
          <span class="ico">{m.icon}</span>
          <strong>{m.label}</strong>
          <small>{m.desc}</small>
        </button>
      {/each}
      {/key}
    </div>
  </section>
{/if}

<style>
  .plus-panel {
    margin-bottom: 1rem;
    padding: 1.1rem 1.15rem 1rem;
    border-radius: var(--radius-xl);
    border: 1px solid var(--glass-border);
    background: var(--glass-bg-card);
    backdrop-filter: var(--glass-blur);
    -webkit-backdrop-filter: var(--glass-blur);
    box-shadow: var(--glass-shadow);
  }
  header { margin-bottom: 0.85rem; }
  h2 {
    margin: 0.35rem 0 0.25rem;
    font-family: Poppins, Inter, system-ui, sans-serif;
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--text-primary);
  }
  header p {
    margin: 0;
    font-size: 0.85rem;
    color: var(--text-secondary);
    line-height: 1.45;
  }
  .plus-badge {
    display: inline-block;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 0.22rem 0.55rem;
    border-radius: 999px;
    background: linear-gradient(135deg, #5b21b6, #7c3aed);
    color: #fff;
    box-shadow: 0 2px 8px rgba(124,58,237,0.3);
  }
  .mod-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
    gap: 0.55rem;
  }
  .mod-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.2rem;
    text-align: left;
    padding: 0.65rem 0.75rem;
    border: 1px solid var(--glass-border);
    border-radius: 0.85rem;
    background: rgba(255,255,255,0.55);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    cursor: pointer;
    font: inherit;
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
  }
  .mod-card:hover {
    border-color: var(--brand-strong);
    box-shadow: 0 6px 20px rgba(124,58,237,0.18);
    transform: translateY(-2px);
    background: rgba(255,255,255,0.75);
  }
  .ico { font-size: 1.25rem; line-height: 1; }
  .mod-card strong { font-size: 0.82rem; color: var(--text-primary); }
  .mod-card small { font-size: 0.72rem; color: var(--text-secondary); line-height: 1.35; }
</style>
